import { useEffect, useRef, useState, type ReactNode } from "react";
import { formatPhone } from "@shared/phone";
import { formatMoneyToRead } from "@shared/currency";
import { Building2, CalendarDays, ChevronDown, Trash2, User } from "lucide-react";
import { FullTextCell } from "./FullText";

/**
 * s221 Lane 1b — THE INTERVIEW'S CELLS, drawn as the SPEC board draws them (`Main.dc.html`, D-221.28).
 *
 * 🔴 A NAMED G7 EXCEPTION (Reade, s221 Lane 1b ruling 2). The planner pages render their cells through
 * `EditableCell`, whose date, select and yes/no are the browser's stock controls. The board draws its
 * own: a 44px box, 8px radius, stone border, a person or building icon, a `yyyy-mm-dd` date with a
 * calendar mark, a "Choose…" picker with a chevron, a two-button Yes / No segment. Restyling
 * `EditableCell` would repaint every planner page — that is the rebrand's job, not this lane's — so the
 * interview carries these ONE place, here, used by every interview screen. The rebrand folds them back
 * into the shared cell. Never copy them into a screen.
 *
 * Commit rules are the Lane 1 ones: a typed box commits the value it holds on blur (never a state read
 * a tick behind); a picker or segment commits on change, because the choice IS the answer.
 */

/** The board's control box: 44px, 8px radius, stone border, warm-white ground (rule 124: 44px floor). */
export const CELL_BOX =
  "h-11 w-full rounded-[8px] border border-warm-stone bg-warm-white text-[15px] text-warm-charcoal " +
  "placeholder:text-warm-muted focus:outline-none focus:border-sage-600 focus:ring-1 focus:ring-sage-600";

const ICON = "pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-warm-muted";

export type CellIcon = "person" | "org" | "none";

/** A typed box, optionally with the board's person or building mark at its left. */
export function IconTextCell({
  value,
  onCommit,
  placeholder,
  icon = "none",
  testId,
  ariaLabel,
  phone = false,
  money = false,
}: {
  value: string;
  onCommit: (value: string) => void;
  placeholder?: string;
  icon?: CellIcon;
  testId?: string;
  ariaLabel?: string;
  /** s228 (item 69): a phone number — the product's ONE convention (`shared/phone.ts` formatPhone) on blur, a tel
   *  keyboard, and no browser autofill, exactly as the contact card's phone box (PersonPicker). */
  phone?: boolean;
  /**
   * s233 (D-233.16 points 5-6, D-233.34): a money box. It shows the stored amount as dollars through #153's ONE reader
   * (`formatMoneyToRead`: "$850,000", cents only when not zero) and commits digits ("$1,900" -> "1900"). Words are never
   * guessed ("850k" is not 850,000): the box keeps what was typed, says "Numbers only" under itself and saves nothing.
   */
  money?: boolean;
}) {
  const shown = (v: string) => (money && v ? formatMoneyToRead(v) : v);
  const [draft, setDraft] = useState(shown(value));
  const [wordsTyped, setWordsTyped] = useState(false);
  useEffect(() => { setDraft(shown(value)); setWordsTyped(false); }, [value]);
  const Mark = icon === "person" ? User : icon === "org" ? Building2 : null;
  return (
    <div className="relative">
      {Mark && <Mark className={`${ICON} left-3`} aria-hidden="true" />}
      <input
        className={`${CELL_BOX} ${Mark ? "pl-9" : "pl-3"} pr-3`}
        value={draft}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          if (money) {
            const amount = moneyDigits(e.target.value);
            if (amount === null) { setWordsTyped(true); return; }
            setWordsTyped(false);
            setDraft(shown(amount));
            if (amount !== (value ?? "").trim() && !(amount && value && Number(amount) === Number(value))) onCommit(amount);
            return;
          }
          const v = phone ? formatPhone(e.target.value) : e.target.value;
          if (v !== draft) setDraft(v);
          if (v !== value) onCommit(v);
        }}
        {...(phone ? { type: "tel", inputMode: "tel" as const, autoComplete: "off" } : {})}
        {...(money ? { inputMode: "decimal" as const, autoComplete: "off", "aria-invalid": wordsTyped || undefined } : {})}
        data-testid={testId}
      />
      {money && wordsTyped && (
        <span className="mt-1 block text-[13px] text-warm-muted" role="status" data-testid={testId ? `${testId}-numbers-only` : undefined}>
          {MONEY_NUMBERS_ONLY}
        </span>
      )}
    </div>
  );
}

/** s233 (D-233.34(9)): the calm line under a money box that was given words. */
export const MONEY_NUMBERS_ONLY = "Numbers only";

/**
 * s233: what a money box stores — the digits, read as the typed tables read them (D-229.111(2): "$", "," and spaces
 * dropped, a trailing "." dropped). "" for an empty box; `null` for anything else with words in it — never a guess.
 */
export function moneyDigits(typed: string): string | null {
  const t = typed.trim();
  if (t === "") return "";
  const n = t.replace(/[$,\s]/g, "").replace(/\.$/, "");
  return /^-?\d+(\.\d+)?$/.test(n) ? n : null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The board's date: typed as `yyyy-mm-dd`, with a calendar mark that opens the browser's picker. The
 * stored value is always ISO — the same string the planner page stores — and a half-typed or malformed
 * date is never written; the box goes back to what was stored.
 */
export function DateCell({ value, onCommit, testId, ariaLabel = "Date" }: { value: string; onCommit: (v: string) => void; testId?: string; ariaLabel?: string }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const native = useRef<HTMLInputElement>(null);
  const commit = (v: string) => {
    const t = v.trim();
    if (t === value) return;
    if (t === "" || ISO_DATE.test(t)) onCommit(t);
    else setDraft(value);
  };
  return (
    <div className="relative">
      <input
        className={`${CELL_BOX} pl-3 pr-9`}
        value={draft}
        placeholder="yyyy-mm-dd"
        inputMode="numeric"
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        data-testid={testId}
      />
      <button
        type="button"
        aria-label="Open the calendar"
        onClick={() => { try { native.current?.showPicker?.(); } catch { native.current?.focus(); } }}
        className="absolute right-1 top-1/2 flex h-9 w-8 -translate-y-1/2 items-center justify-center text-warm-muted hover:text-warm-charcoal"
        data-testid={testId ? `${testId}-calendar` : undefined}
      >
        <CalendarDays className="h-4 w-4" aria-hidden="true" />
      </button>
      {/* The browser's own calendar, invisible: the mark opens it; its choice lands as ISO. */}
      <input
        ref={native}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-0 h-0 w-0 opacity-0"
        value={ISO_DATE.test(draft) ? draft : ""}
        onChange={(e) => { setDraft(e.target.value); commit(e.target.value); }}
      />
    </div>
  );
}

/** A picker option: a plain value, or a stored value with the words the board shows for it. */
export type PickOption = string | { value: string; label: string };

/** The board's picker: "Choose…", a chevron, the choice commits on change. The STORED value can differ
 *  from the words shown (s221 Lane 1c: "My son" is shown, the shared choice "Son" is stored). */
export function PickerCell({
  value,
  options,
  onCommit,
  placeholder = "Choose…",
  testId,
  ariaLabel,
}: {
  value: string;
  options: readonly PickOption[];
  onCommit: (value: string) => void;
  placeholder?: string;
  testId?: string;
  ariaLabel?: string;
}) {
  return (
    <div className="relative">
      <select
        className={`${CELL_BOX} appearance-none pl-3 pr-9 ${value ? "" : "text-warm-muted"}`}
        value={value}
        aria-label={ariaLabel}
        onChange={(e) => { if (e.target.value !== value) onCommit(e.target.value); }}
        data-testid={testId}
      >
        {/* s233 (D-233.15): the placeholder is the empty state — greyed and disabled, never a choice (every dropdown, G7). */}
        <option value="" disabled>{placeholder}</option>
        {options.map((o) => {
          const { value: v, label } = typeof o === "string" ? { value: o, label: o } : o;
          return <option key={v} value={v}>{label}</option>;
        })}
      </select>
      <ChevronDown className={`${ICON} right-3`} aria-hidden="true" />
    </div>
  );
}

/** The board's segment (Yes / No): two joined buttons, the picked one filled. Commits on click.
 *  s237 (`fit`, chapter one's "Is this home…", Main.dc.html): the buttons hug their words instead of filling the row. */
export function SegmentCell({
  value,
  options,
  onCommit,
  testId,
  ariaLabel,
  fit,
}: {
  value: string;
  options: readonly { value: string; label: string }[];
  onCommit: (value: string) => void;
  testId?: string;
  ariaLabel?: string;
  fit?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={`${fit ? "inline-flex self-start" : "flex"} h-11 overflow-hidden rounded-[8px] border border-warm-stone bg-warm-white`} data-testid={testId}>
      {options.map((o, i) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => { if (!on) onCommit(o.value); }}
            className={
              (fit ? "whitespace-nowrap px-[18px] " : "flex-1 ") + "text-[14px] " + (i > 0 ? "border-l border-warm-stone " : "") +
              (on ? "bg-sage-600 text-warm-white" : "text-warm-charcoal hover:bg-warm-stone-light")
            }
            data-testid={testId ? `${testId}-${o.value}` : undefined}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** A secondary action in the board's row style (Note, + Add row). */
export function RowActionButton({
  children,
  onClick,
  strong,
  testId,
  ariaExpanded,
  title,
}: {
  children: ReactNode;
  onClick: () => void;
  strong?: boolean;
  testId?: string;
  ariaExpanded?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-expanded={ariaExpanded}
      className={
        // s226 (rule 124, ported): the board's row buttons are 13px, padded 12px (Add row, `strong`) and 10px (Note)
        // (Main.dc.html, the row actions). At 14px and 12px they pushed the × past the table's edge.
        "inline-flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[8px] border bg-warm-white text-[13px] text-warm-charcoal hover:bg-warm-stone-light " +
        (strong ? "border-sage-600 px-3" : "border-warm-stone px-2.5")
      }
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/**
 * The board's tile note: a tinted card with the tile's mark, placed AFTER the tables (Main.dc.html,
 * `noteFor`). One per picked tile that carries a note.
 */
export function NoteCard({ mark, color, children, testId }: { mark: string; color: string; children: ReactNode; testId?: string }) {
  return (
    <div className="flex max-w-[1120px] items-start gap-2.5 rounded-[8px] bg-warm-stone-light px-3.5 py-3" data-testid={testId}>
      <span
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] text-[8px] font-medium text-warm-white"
        style={{ backgroundColor: color }}
      >
        {mark}
      </span>
      <p className="text-[14px] leading-relaxed text-warm-charcoal">{children}</p>
    </div>
  );
}

/**
 * s223 — the board's READ cell (Main.dc.html `readCell`): an answer the plan already holds, shown and never asked
 * again (rule 42 — one fact, one place). Plain text in the row, no box; "—" when there is nothing to show.
 */
// s226 (board v334, rule 124): the empty dash is warm.muted (#6B655A; #4A4640 since s233, D-233.37) (Main.dc.html `readCell`); it was stone.
// s227 (D14): the board draws it on ONE line with an ellipsis (Main.dc.html:674, `white-space: nowrap; overflow:
// hidden; text-overflow: ellipsis`). With `reveal`, a CUT value is its own way to read it — the shared FullTextCell
// (rule 133, D-223.13); the caller draws the full value under the row with FullTextPanel (`${testId}-text`).
export function ReadCell({ text, testId, reveal }: {
  text: string;
  testId?: string;
  reveal?: { open: boolean; onToggle: () => void; title: string };
}) {
  const has = text.trim() !== "";
  return (
    <div className="flex min-h-[44px] min-w-0 items-center" aria-readonly="true" data-testid={testId}>
      {has && reveal ? (
        <FullTextCell text={text} open={reveal.open} onToggle={reveal.onToggle} title={reveal.title}
          textClassName="block min-w-0 truncate text-[14px] text-warm-charcoal" testId={`${testId ?? "read"}-text`} />
      ) : (
        <span className={`truncate text-[14px] ${has ? "text-warm-charcoal" : "text-warm-muted"}`}>{has ? text : "—"}</span>
      )}
    </div>
  );
}

/**
 * s223 — the board's OFF cell (Main.dc.html `NA()`): a question this row does not ask — shown while an `onWhen`
 * answer is not given. The board's own words.
 */
// s226 (board v331): the board's words are now "not asked" (Main.dc.html, the `c.isOff` cell), and with them the
// s223 deviation goes: that cell narrowed its padding to 8px only because "not asked here" needed 113px in a
// 110px column. The box is back to the board's 12px (Reade: take it back). Board v337: the words are warm.muted (#4A4640 since s233),
// warm-muted — the light grey #D9D1C1 is never a text colour (the border keeps it).
export function OffCell({ testId }: { testId?: string }) {
  return (
    <div className="flex min-h-[44px] min-w-0 items-center overflow-hidden whitespace-nowrap rounded-[8px] border border-warm-stone bg-warm-stone-light px-3 text-[13px] text-warm-muted"
      data-testid={testId}>
      not asked
    </div>
  );
}

/**
 * s235 (Reade, chapter-one walk item 9; G7) — THE INTERVIEW'S ONE REMOVE ×. Lifted out of the row table
 * (`InterviewRowTable`, its row ×) so an added other-email box on Personal details takes the same control, not a
 * second drawing of it. 44px tall, 32px wide, the muted × darkening on hover; `label` is what a screen reader says.
 */
// s237 (D-236.95 / .96, rule 129 as amended): every remove in the interview is ONE bin — the board's trash icon
// (Main.dc.html:453–454, lucide trash-2, 16px), with the board's hover title ("Remove this row", or "Remove this
// email" on the extra-email box, Main.dc.html:206). What a remove does ("… removed · Undo") is unchanged.
export function RemoveButton({ onClick, label, testId, title = "Remove this row" }: { onClick: () => void; label: string; testId?: string; title?: string }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={title}
      className="flex h-11 w-8 shrink-0 items-center justify-center text-warm-muted hover:text-warm-charcoal"
      data-testid={testId}>
      <Trash2 className="h-4 w-4" aria-hidden="true" data-icon="bin" />
    </button>
  );
}
